<template>
  <div>
    <div class="d-flex mb-3 mt-1">
      <SearchBox
        ref="search"
        v-model="search"
        class="flex-grow-1"
      />
    </div>
    <div v-if="!singleSelect">
      <div class="row no-gutters align-items-center">
        <div class="col-7 d-flex align-items-center">
          <Checkbox
            :value="allSelected"
            class="pr-1 align-items-center"
            @update:value="toggleAll"
          >
            <div class="mb-0 label sm-label">
              {{ $t('general.selectAll') }}
            </div>
          </Checkbox>
        </div>
      </div>
      <hr class="mb-1 mt-2">
    </div>
    <div
      v-if="products.length > 0 && !onlyListings"
      style="height: 36px"
      class="d-flex"
    >
      <div
        class="secondary-icon bill-button w-50"
        :class="{
          active: type === 'offer',
        }"
        @click="type = 'offer'"
      >
        {{ $t('shop.offer.offer') }}
      </div>
      <div
        class="secondary-icon bill-button w-50"
        :class="{ active: type === 'products' }"
        @click="type = 'products'"
      >
        {{ $t('shop.products') }}
      </div>
    </div>
    <div
      :style="{
        overflowY: 'auto',
        height: listHeight
      }"
    >
      <div
        v-if="type === 'products'"
        style="margin-top: 0px;"
      >
        <div
          v-for="s in filteredProducts"
          :key="s.id"
          class="sort-option text-left d-flex align-items-center pl-0"
          @click="toggle(s)"
        >
          <Checkbox
            :value.sync="checked[s.id]"
            :emit="false"
          />
          <div class="flex-grow-1 pl-2">
            {{ s.name }}
          </div>
        </div>
      </div>
      <div v-else>
        <div
          v-if="subscriptions && filteredListings.length === 0"
          class="text-center mt-2 text-secondary"
        >
          {{ $t('shop.noSubscriptions') }}
        </div>
        <div
          v-for="s in filteredListings"
          :key="s.id"
          class="sort-option text-left d-flex align-items-center pl-0"
          @click="toggleListing(s.id)"
        >
          <Checkbox
            :value="checked[s.id]"
            :emit="false"
          />
          <div class="ml-2">
            <div class="flex-grow-1">
              {{ s.name }}
            </div>
            <div
              v-if="s.tags.length > 0"
              class="d-flex align-items-center mt-0"
            >
              <div
                v-for="tag in s.tags"
                :key="tag.name"
                class="badge badge-outline-secondary badge-pill mr-1"
              >
                <i
                  :class="tag.icon"
                  class="mr-1"
                />
                {{ tag.name }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Checkbox from '@/components/utils/Checkbox';
import createSearch from '@/utils/search';
import { mapGetters } from 'vuex';

export default {
  props: {
    catalogId: String,
    selected: Array,
    withCalendar: Boolean,
    subscriptions: Boolean,
    onlyListings: Boolean,
    singleSelect: Boolean,
  },
  data: () => ({
    checked: {},
    type: 'offer',
    search: '',
  }),
  components: {
    Checkbox,
  },
  computed: {
    ...mapGetters('shop', [
      'products',
      'listings',
      'catalogDetails',
    ]),
    listHeight() {
      if (this.withCalendar) {
        return 'calc(100vh - 620px)';
      }
      if (this.onlyListings) {
        return '400px';
      }
      return 'calc(100vh - 230px)';
    },
    highlightSubCatalogs() {
      return [
        this.catalogDetails?.metadata?.dietId,
        this.catalogDetails?.metadata?.mealSizeId,
        this.catalogDetails?.metadata?.menuId,
      ].filter((x) => x);
    },
    filteredListings() {
      if (!this.listings) return [];
      const search = createSearch(this.search);

      return this.listings
        .filter((x) => !this.subscriptions || x.subscription?.enabled)
        .filter((x) => this.search.length === 0 || search(x.name))
        .map((x) => ({
          ...x,
          tags: x.assignments
            .filter((a) => this.highlightSubCatalogs.includes(a.subCatalogId))
            .map((a) => ({
              name: this.catalogDetails.subCatalogNodes.find((n) => n.nodeId === a.nodeId)?.name,
              icon: this.getIcon(a.subCatalogId),
            }))
            .filter((a) => a),
        }));
    },
    filteredProducts() {
      if (!this.products) return [];
      const search = createSearch(this.search);

      return this.products
        .filter((x) => this.search.length === 0 || search(x.name));
    },
    allSelected() {
      const items = this.type === 'products' ? this.filteredProducts : this.filteredListings;
      if (items.length === 0) return false;
      return items.every((item) => this.checked[item.id]);
    },
  },
  watch: {
    type() {
      this.checked = {};
      this.$emit('update:selected', []);
      this.$emit('update:selectedListings', []);
    },
  },
  methods: {
    getIcon(subCatalogId) {
      if (subCatalogId === this.catalogDetails?.metadata?.dietId) {
        return 'far fa-plate-wheat';
      }
      if (subCatalogId === this.catalogDetails?.metadata?.mealSizeId) {
        return 'far fa-arrow-up-small-big';
      }
      if (subCatalogId === this.catalogDetails?.metadata?.menuId) {
        return 'far fa-calendar-range';
      }
      return null;
    },
    toggle(s) {
      this.$set(this.checked, s.id, !this.checked[s.id]);
      this.$emit('update:selected', Object.keys(this.checked).filter((x) => this.checked[x]));
    },
    toggleListing(id) {
      if (this.singleSelect) {
        this.checked = {};
      }
      this.$set(this.checked, id, !this.checked[id]);
      this.$emit('update:selectedListings', Object.keys(this.checked).filter((x) => this.checked[x]));
    },
    toggleAll() {
      const items = this.type === 'products' ? this.filteredProducts : this.filteredListings;
      const shouldSelectAll = !this.allSelected;

      items.forEach((item) => {
        this.$set(this.checked, item.id, shouldSelectAll);
      });

      if (this.type === 'products') {
        this.$emit('update:selected', Object.keys(this.checked).filter((x) => this.checked[x]));
      } else {
        this.$emit('update:selectedListings', Object.keys(this.checked).filter((x) => this.checked[x]));
      }
    },
  },
  created() {
    this.selected.forEach((x) => this.$set(this.checked, x, true));
  },
};
</script>

<style lang="scss" scoped>
 @import '~@/components/payers/style.scss';

  .bill-button {
    font-size: 14px;
    font-weight: 500;

    &.active {
      background-color: rgba($blue, 0.2);
      color: $blue;
    }
  }

  .sort-option-selected {
    position: relative;
    min-height: auto;
    padding: 10px;
    border-radius: 50%;
  }

  .close-btn {
    position: absolute;
    cursor: pointer;
    font-size: 20px;
    width: 24px;
    right: 10px;
    top: 11px;
    padding: 2px;
    text-align: center;
    border-radius: 50%;
    color: #888;

    &:hover {
      color: #333;
      background-color: #ddd;
    }
  }

  .sm-label {
    font-size: 12px;
  }
</style>
